import { FC, useState } from "react";
import { QRCodeSVG } from "qrcode.react";
import type { ApplicationResponse } from "../types";
import { formatPrice } from "../utils/validation";
import { markPaymentSubmitted } from "../services/api";
import { useBranding } from "../context/BrandingContext";

interface PaymentQRProps {
  paymentData: ApplicationResponse;
  onReset: () => void;
}

type PaymentStep = "qr" | "confirming" | "submitted";

/**
 * Payment QR code display with "I Have Paid" confirmation flow
 */
const PaymentQR: FC<PaymentQRProps> = ({ paymentData, onReset }) => {
  const brand = useBranding();
  const [step, setStep] = useState<PaymentStep>("qr");
  const [error, setError] = useState<string | null>(null);

  const handlePaymentConfirm = async () => {
    setStep("confirming");
    setError(null);

    try {
      await markPaymentSubmitted(paymentData.applicationId);
      setStep("submitted");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to confirm payment");
      setStep("qr");
    }
  };

  // Payment submitted success state
  if (step === "submitted") {
    return (
      <div className="mx-auto max-w-md rounded-2xl bg-white p-8 shadow-lg border border-neutral-200">
        <div className="text-center">
          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-accent-100">
            <svg className="h-10 w-10 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
          </div>
          <h2 className="mt-6 text-2xl font-bold text-neutral-900">Payment Submitted!</h2>
          <p className="mt-3 text-neutral-600">
            Thank you for your payment. Our team will verify it and send you a confirmation email shortly.
          </p>
        </div>

        <div className="mt-8 rounded-lg bg-accent-50 p-4 border border-accent-200">
          <div className="flex items-start gap-3">
            <svg className="h-5 w-5 text-accent-600 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <div className="text-sm text-accent-800">
              <p className="font-medium">What happens next?</p>
              <ul className="mt-2 list-disc list-inside space-y-1">
                <li>Our team will verify your payment within 24 hours</li>
                <li>You will receive a confirmation email</li>
                <li>Course access details will be shared with you</li>
              </ul>
            </div>
          </div>
        </div>

        <div className="mt-6 space-y-3 rounded-lg bg-neutral-50 p-4">
          <div className="flex justify-between text-sm">
            <span className="text-neutral-600">Application ID</span>
            <span className="font-mono text-neutral-900">{paymentData.applicationId.slice(0, 8)}...</span>
          </div>
          <div className="flex justify-between text-sm">
            <span className="text-neutral-600">Course</span>
            <span className="font-medium text-neutral-900">{paymentData.courseName}</span>
          </div>
          <div className="flex justify-between">
            <span className="text-sm text-neutral-600">Amount</span>
            <span className="text-lg font-bold text-brand-secondary">
              {formatPrice(paymentData.amount)}
            </span>
          </div>
        </div>

        <div className="mt-6 text-center text-sm text-neutral-500">
          <p>Questions? Contact us at</p>
          <a href={`mailto:${brand.email}`} className="font-medium text-brand-secondary hover:underline">
            {brand.email}
          </a>
        </div>

        <button
          onClick={onReset}
          className="mt-6 w-full rounded-lg border border-neutral-300 bg-white px-4 py-3 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
        >
          Submit Another Application
        </button>
      </div>
    );
  }

  // QR Code display
  return (
    <div className="mx-auto max-w-md rounded-2xl bg-white p-8 shadow-lg border border-neutral-200">
      {/* Test Mode Banner */}
      {paymentData.isTestMode && (
        <div className="mb-6 -mx-8 -mt-8 rounded-t-2xl bg-amber-500 px-4 py-3 text-center">
          <div className="flex items-center justify-center gap-2 text-amber-900">
            <svg className="h-5 w-5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
            </svg>
            <span className="font-bold">TEST PAYMENT MODE</span>
          </div>
          <p className="mt-1 text-sm text-amber-800">
            ₹1 only (Development) — Not for production use
          </p>
        </div>
      )}

      {/* Header */}
      <div className="text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-accent-100">
          <svg className="h-8 w-8 text-accent-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
          </svg>
        </div>
        <h2 className="mt-4 text-xl font-semibold text-neutral-900">Application Submitted!</h2>
        <p className="mt-2 text-sm text-neutral-600">
          Scan the QR code below with any UPI app to complete your payment
        </p>
      </div>

      {/* QR Code */}
      <div className="mt-8 flex justify-center">
        <div className="rounded-xl bg-white p-4 shadow-inner ring-1 ring-neutral-200">
          <QRCodeSVG
            value={paymentData.upiLink}
            size={200}
            level="H"
            includeMargin={true}
          />
        </div>
      </div>

      {/* UPI ID Display */}
      <div className="mt-4 text-center">
        <p className="text-xs text-neutral-500">UPI ID</p>
        <p className="font-mono text-sm font-medium text-neutral-700">hereandnow@cub</p>
      </div>

      {/* Payment Details */}
      <div className="mt-6 space-y-3 rounded-lg bg-neutral-50 p-4">
        <div className="flex justify-between">
          <span className="text-sm text-neutral-600">Course</span>
          <span className="text-sm font-medium text-neutral-900">{paymentData.courseName}</span>
        </div>
        {/* Show original price if in test mode */}
        {paymentData.isTestMode && paymentData.originalPrice !== paymentData.amount && (
          <div className="flex justify-between">
            <span className="text-sm text-neutral-600">Course Price</span>
            <span className="text-sm text-neutral-500 line-through">
              {formatPrice(paymentData.originalPrice)}
            </span>
          </div>
        )}
        <div className="flex justify-between items-center">
          <span className="text-sm text-neutral-600">
            {paymentData.isTestMode ? "Test Amount" : "Amount"}
          </span>
          <div className="text-right">
            <span className="text-lg font-bold text-brand-secondary">
              {formatPrice(paymentData.amount)}
            </span>
            {paymentData.isTestMode && (
              <span className="ml-2 inline-block rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-800">
                TEST
              </span>
            )}
          </div>
        </div>
        <div className="flex justify-between">
          <span className="text-sm text-neutral-600">Application ID</span>
          <span className="font-mono text-xs text-neutral-500">
            {paymentData.applicationId.slice(0, 8)}...
          </span>
        </div>
      </div>

      {/* Instructions */}
      <div className="mt-6 rounded-lg bg-blue-50 p-4 border border-blue-100">
        <h4 className="text-sm font-medium text-blue-900">How to pay:</h4>
        <ol className="mt-2 list-inside list-decimal space-y-1 text-sm text-blue-800">
          <li>Open any UPI app (Google Pay, PhonePe, Paytm)</li>
          <li>Scan the QR code above</li>
          <li>Verify amount and complete payment</li>
          <li>Click "I Have Paid" below</li>
        </ol>
      </div>

      {/* Error Message */}
      {error && (
        <div className="mt-4 rounded-lg bg-red-50 p-3 text-sm text-red-700 border border-red-200">
          {error}
        </div>
      )}

      {/* Action Buttons */}
      <div className="mt-6 space-y-3">
        <button
          onClick={handlePaymentConfirm}
          disabled={step === "confirming"}
          className="w-full rounded-lg px-4 py-3 text-sm font-semibold text-white shadow-lg transition-all disabled:opacity-50"
          style={{ backgroundColor: brand.colors.primary }}
        >
          {step === "confirming" ? (
            <span className="flex items-center justify-center gap-2">
              <span className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
              Confirming...
            </span>
          ) : (
            "✓ I Have Paid"
          )}
        </button>

        <button
          onClick={onReset}
          className="w-full rounded-lg border border-neutral-300 bg-white px-4 py-3 text-sm font-medium text-neutral-700 hover:bg-neutral-50 transition-colors"
        >
          Cancel & Start Over
        </button>
      </div>
    </div>
  );
};

export default PaymentQR;
